/**
 * Build 100 (Daniel 2026-10-05): one list of lead sources, grouped the way
 * KW Command groups them.
 *
 * "Can we have a dropdown for the lead source, similar to what we have in
 * Command? instead of a box can you prepopulate some lead sources? and have
 * an other we can fill in our selves but think of potential lead sources top
 * agents use."
 *
 * Used by the contact form and all three lead forms, so a source is spelled
 * the same way everywhere. That matters: Business health groups lead source
 * ROI by the exact text (lower-cased), so "Open House", "open house" and
 * "Open house event" were three sources with a third of the numbers each.
 *
 * The values already in use before this build are kept letter for letter
 * (TikTok, Instagram, Facebook, Zillow, Realtor.com, Facebook Ad, Open House,
 * Referral, Past client, Sphere, Website, Door knock, Cold call), so existing
 * leads and contacts still match. Business health reads "referral", "sphere",
 * "past client", "repeat", "friend" and "family" as sphere business and
 * "facebook" / "instagram" as Meta, so the new names were chosen to land in
 * the right bucket.
 */
export interface LeadSourceGroup { label: string; sources: string[] }

export const LEAD_SOURCE_GROUPS: LeadSourceGroup[] = [
  {
    label: 'Sphere and referrals',
    sources: [
      'Sphere',
      'Past client',
      'Repeat client',
      'Referral',
      'Referral: past client',
      'Referral: friend or family',
      'Referral: KW agent',
      'Referral: out-of-area agent',
      'Referral: lender',
      'Referral: vendor or partner',
      'Referral: attorney or CPA',
    ],
  },
  {
    label: 'Online and social',
    sources: [
      'Website',
      'Website: home valuation',
      'Website: home search',
      'Google search',
      'Google Business Profile',
      'Facebook',
      'Instagram',
      'TikTok',
      'YouTube',
      'LinkedIn',
      'Nextdoor',
      'Email newsletter',
    ],
  },
  {
    label: 'Paid ads',
    sources: [
      'Facebook Ad',
      'Instagram Ad',
      'Google Ad',
      'YouTube Ad',
    ],
  },
  {
    label: 'Portals',
    sources: [
      'Zillow',
      'Realtor.com',
      'Homes.com',
      'Redfin',
      'KW.com or KW app',
      'Opcity',
    ],
  },
  {
    label: 'Auction',
    sources: [
      'Auction registration',
      'Auction sign',
      'Auction marketing',
    ],
  },
  {
    label: 'Prospecting',
    sources: [
      'Cold call',
      'Circle prospecting',
      'Door knock',
      'Expired listing',
      'FSBO',
      'Withdrawn or canceled listing',
      'Probate or estate',
      'Pre-foreclosure',
      'Absentee owner',
      'Geographic farm',
      'Direct mail',
    ],
  },
  {
    label: 'Events and in person',
    sources: [
      'Open House',
      'Sign call',
      'Client event',
      'Community event or sponsorship',
      'Seminar or class',
      'Networking group',
      'Walk-in',
    ],
  },
  {
    label: 'Investors and builders',
    sources: [
      'Investor network',
      'Wholesaler',
      'Builder or new construction',
    ],
  },
];

/** Every preset, in group order. */
export const LEAD_SOURCES: string[] = LEAD_SOURCE_GROUPS.flatMap((g) => g.sources);

/** True when `value` is one of the presets (ignoring case and spacing). */
export function isPresetSource(value: string): boolean {
  const v = value.trim().toLowerCase();
  return LEAD_SOURCES.some((s) => s.toLowerCase() === v);
}

/** The preset spelling for a value typed another way, or the value itself. */
export function canonicalSource(value: string): string {
  const v = value.trim();
  return LEAD_SOURCES.find((s) => s.toLowerCase() === v.toLowerCase()) ?? v;
}
